import { PlacePrediction } from "./api";

export interface Vet {
  id: number;
  name: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  /** vorberechnetes, umlaut-gefaltetes Suchfeld: name + stadt + plz (+ evtl. Aliasse) */
  q: string;
  /** zur Laufzeit ergänztes Suchfeld inkl. Straße (name + stadt + plz + strasse) */
  search?: string;
}

// Die bereinigte Praxis-Liste wird nur einmal (lazy) geladen und gecacht.
let vetsPromise: Promise<Vet[]> | undefined;

export function loadVets(): Promise<Vet[]> {
  if (vetsPromise === undefined) {
    vetsPromise = fetch("/vets.json")
      .then((response) => (response.ok ? (response.json() as Promise<Vet[]>) : []))
      .then((vets) =>
        // Straße einmalig in ein Suchfeld aufnehmen, damit auch über die Adresse gefunden wird.
        vets.map((vet) => ({ ...vet, search: `${vet.q} ${foldQuery(vet.street)}`.trim() }))
      )
      .catch(() => []);
  }
  return vetsPromise;
}

/** Umlaute/Sonderzeichen falten – identisch zur Aufbereitung der vets.json. */
export function foldQuery(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const PREDICTION_PREFIX = "vet:";

export function vetPlaceId(vet: Vet): string {
  return `${PREDICTION_PREFIX}${vet.id}`;
}

export function vetIdFromPlaceId(placeId: string): number | undefined {
  if (!placeId.startsWith(PREDICTION_PREFIX)) return undefined;
  const id = Number(placeId.slice(PREDICTION_PREFIX.length));
  return Number.isNaN(id) ? undefined : id;
}

/**
 * Tolerante Suche über die Praxen: es müssen alle Such-Wörter vorkommen
 * (Reihenfolge egal). Treffer, die mit dem Namen beginnen, ranken oben.
 */
export function searchVets(vets: Vet[], query: string, limit = 8): Vet[] {
  const folded = foldQuery(query);
  if (folded.length < 2) return [];

  const words = folded.split(" ");
  const scored: { vet: Vet; score: number }[] = [];

  for (const vet of vets) {
    // gesucht wird über Name + Ort + PLZ + Straße; Ranking bleibt namensbasiert
    const haystack = vet.search ?? vet.q;
    if (!words.every((word) => haystack.includes(word))) continue;

    let score = 0;
    if (vet.q.startsWith(folded)) score += 100; // exakter Präfix des Namens-Felds
    if (vet.q.startsWith(words[0])) score += 50; // Name beginnt mit erstem Wort
    score -= vet.q.length / 1000; // kürzere/genauere Treffer leicht bevorzugen
    scored.push({ vet, score });
  }

  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.vet);
}

/** Wandelt eine Praxis in das bestehende PlacePrediction-Format um (fürs Dropdown-UI). */
export function vetToPrediction(vet: Vet): PlacePrediction {
  const address = [vet.street, [vet.zip, vet.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const description = [vet.name, address].filter(Boolean).join(" · ");
  return { description, placeId: vetPlaceId(vet), matchedSubstrings: [] };
}
